import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Select, Input, CircleButton, Skeleton } from '@ui';
import { useSetValueTagMetricMutation } from '@app/store/api/metrics.api';
import { toast } from 'react-toastify';
import useOnClickOutside from '@app/hooks/use-onclick-outside';
import { SettingsMetricType } from '@app/interfaces/pages-types/anatylics-metric.type';

import Icon from '@app/components/ui/icons';

import useTranslation from '@app/hooks/use-translation';

import { useSetValueTagChatMetricMutation } from '@app/store/api/chat-api/chat-metric.api';

import { pictogramsData } from './pictograms.data';

type MetricEditValueProps = {
  settings: SettingsMetricType | null;
  project_id: string;
  metric_id: string;
  record_id: string;
  currentValue: number | string | string[] | number[];
  onCancel?: () => void;
  onChanged?: () => void;
  valueType?: string;
  valueMaxWidth?: number;
  columnWidth?: number;
  withPadding?: boolean;
  index?: number;
  limit?: number;
  page?: number;
  visualization?: 'native' | 'mix' | 'icons';
  triggerReload?: React.Dispatch<React.SetStateAction<number>>;
  currentTemplate: 'chat' | 'voice';
};
const MetricEditValue: FC<MetricEditValueProps> = React.memo((props) => {
  const {
    currentValue,
    valueType,
    settings,
    currentTemplate,
    page,
    project_id,
    metric_id,
    withPadding = true,
    record_id,
    onChanged,
    onCancel,
    valueMaxWidth,
    columnWidth,
    visualization,
    limit,
    index,
    triggerReload,
  } = props;

  const { t } = useTranslation('pages.metricTemplates.manualMetric');

  const [editMode, changeEditMode] = useState<boolean>(false);
  const [value, changeValue] = useState<string[] | number[] | string | number>(currentValue);
  const [putUpdateValueVoice, { isLoading: isLoadingVoice }] = useSetValueTagMetricMutation();
  const [putUpdateValueChat, { isLoading: isLoadingChat }] = useSetValueTagChatMetricMutation();

  const putUpdateValue = { chat: putUpdateValueChat, voice: putUpdateValueVoice };
  const isLoading = isLoadingChat || isLoadingVoice;

  const editBlockRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLDivElement>(null);

  const [showTooltip, setShowTooltip] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    changeValue(currentValue);
  }, [currentValue]);
  useOnClickOutside(editBlockRef, () => {
    changeEditMode(false);
    changeSelectedStyleToggle(false);
    // changeValue(currentValue);
    onCancel?.();
  });

  const selectOptions = useMemo(() => {
    if (!settings?.select) {
      return [];
    }

    return settings.select.map((selectItem) => ({
      title: visualization === 'icons' ? selectItem.icon : selectItem.value,
      value: selectItem.id,
      icon: visualization === 'mix' ? pictogramsData[selectItem.icon] : undefined,
    }));
  }, [settings?.select, visualization]);

  function changeSelectedStyleToggle(isActive: boolean) {
    if (activeRef?.current?.style) {
      activeRef.current.style['position'] = isActive ? 'absolute' : '';
    }
  }

  async function saveValueHandler() {
    try {
      await putUpdateValue[currentTemplate]({
        params: { project_id, metric_id, record_id },
        body: { value },
      })
        .unwrap()
        .then(() => {
          if (triggerReload) {
            triggerReload((p) => p + 1);
          }
        });

      changeEditMode(false);
      toast.success(t('toast_update_success_message'));
      onChanged?.();
    } catch (error) {
      console.error(error);
      toast.error(t('toast_update_error_message'));
    }
  }

  const conditionalSelectOnChange = (condition: boolean) => {
    return !condition
      ? {
          onChange: (val) => {
            changeValue([val]);
          },
        }
      : {
          onChangeMultiple: (val) => {
            changeValue(val);
          },
        };
  };

  const selectDefaultValueFind = useCallback(() => {
    if (!settings?.select) return [];
    if (value === undefined || value === null) {
      return [];
    }

    if (typeof value !== 'string' && typeof value !== 'number') {
      return settings.multiselect ? value : value?.[0];
    }
    const defaultValue = (value as string)
      ?.split(',')
      .map((w) =>
        settings.multiselect
          ? /[a-zA-ZА-ЯаяёЁ]/gim.test(w)
            ? w.trim()
            : String(parseFloat(w)).trim()
          : /[a-zA-ZА-ЯаяёЁ]/gim.test(w)
          ? w.trim()
          : parseFloat(w),
      );
    const values: number[] = [];
    for (const option of settings.select) {
      if (
        defaultValue?.includes(
          settings.multiselect
            ? String(option.value)
            : /[a-zA-ZА-ЯаяёЁ]/gim.test(option.value.toString())
            ? option.value
            : +option.value,
        )
      ) {
        values.push(option.id);
      }
    }
    return settings?.multiselect ? values : values?.[0];
  }, [settings?.multiselect, settings?.select, value]);

  const handleDropTop = (function () {
    if (!limit || !index || !page) return false;
    const position = Math.ceil(index / page);
    const flag = limit - 4;
    return position >= flag;
  })();
  const foundSelectDefaultValue = useMemo(() => selectDefaultValueFind(), [selectDefaultValueFind]);

  const selectDefaultValue = settings?.multiselect
    ? (foundSelectDefaultValue as string[] | number[])?.length
      ? foundSelectDefaultValue
      : ([settings?.default?.value].filter(Boolean) as number[])
    : foundSelectDefaultValue || (settings?.default?.value as number);

  const valueTypesList = {
    select: (
      <div style={{ maxWidth: columnWidth ? columnWidth * 20 : valueMaxWidth + 'px' }}>
        <Select
          withoutPadding
          size={columnWidth ? columnWidth * 20 : valueMaxWidth}
          options={selectOptions}
          defaultValue={selectDefaultValue}
          {...conditionalSelectOnChange(settings?.multiselect || false)}
          dropWidth={columnWidth ? columnWidth * 19 : valueMaxWidth}
          isMulti={settings?.multiselect}
          dropTop={handleDropTop}
          pictogramsMode={visualization === 'icons'}
        />
      </div>
    ),
    input: (
      <Input
        onChange={(event) => {
          valueType === 'num' ? changeValue(+event.target.value) : changeValue(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.code === 'Enter') {
            saveValueHandler();
          }
        }}
        type={valueType === 'num' ? 'number' : 'text'}
        defaultValue={String(value ?? (settings?.default?.value || ''))}
      />
    ),
  };
  const selectValue =
    typeof value === 'number' || typeof value === 'object'
      ? settings?.multiselect
        ? selectOptions
            ?.filter((option) => (value as number[])?.includes(option.value))
            ?.sort(
              (a, b) =>
                (value as number[])?.indexOf(a.value) - (value as number[])?.indexOf(b.value),
            )
            ?.map(({ title }) => title)
            ?.join(' ')
        : selectOptions?.find((option) => option?.value === value?.[0])?.title?.toString()
      : value;

  const valueCreator = {
    select:
      settings?.input === 'select' && visualization === 'native' ? (
        <div
          style={{ maxWidth: `${columnWidth ? columnWidth * 20 : valueMaxWidth}px` }}
          className="truncate"
        >
          {selectValue ||
            settings.select?.find((value) => value.id === settings.default?.value)?.value}
        </div>
      ) : visualization === 'icons' ? (
        settings?.multiselect ? (
          settings?.select
            ?.filter((i) => (selectDefaultValue as number[]).includes(i.id))
            .map((item) => (
              <Icon
                key={item.id}
                name={pictogramsData?.[item.icon]}
                className="text-action"
                size={30}
              />
            ))
        ) : (
          <Icon
            name={
              pictogramsData?.[
                settings?.select?.find(
                  (item) => item.id === (selectDefaultValueFind() || settings.default?.value),
                )?.icon as number
              ]
            }
            className="text-action"
            size={30}
          />
        )
      ) : (
        <div
          style={{ maxWidth: columnWidth ? columnWidth * 20 : valueMaxWidth + 'px' }}
          className=" h-[40px] flex items-center"
        >
          {settings?.multiselect ? (
            settings?.select
              ?.filter(
                (i) => (value as number[])?.includes(i.id) || settings.default?.value === i.id,
              )
              .map((item) => (
                <div key={item.id} className="flex items-center">
                  <Icon name={pictogramsData?.[item.icon]} className="text-action" size={30} />
                  <div>{item.value}</div>
                </div>
              ))
          ) : (
            <div className="flex items-center">
              <Icon
                name={
                  pictogramsData?.[
                    `${
                      settings?.select?.find(
                        (item) => item.id === (selectDefaultValueFind() || settings.default?.value),
                      )?.icon
                    }`
                  ]
                }
                className="text-action"
                size={30}
              />
              <div>
                {selectValue ||
                  settings?.select?.find((value) => value.id === settings.default?.value)?.value}
              </div>
            </div>
          )}
        </div>
      ),
    input: value || settings?.default?.value,
  };

  if (isLoading)
    return (
      <div className="w-full">
        <Skeleton height={34} width="100%" />
      </div>
    );

  const handleMouseEnter = () => {
    if (textRef.current) {
      const { scrollWidth, clientWidth } = textRef.current;
      if (scrollWidth > clientWidth) {
        setShowTooltip(true);
      }
    }
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <div>
      {editMode && settings?.input ? (
        <div
          ref={editBlockRef}
          className="flex gap-[10px] items-center justify-center relative w-fit"
        >
          <div className="w-full mt-[3px]">{valueTypesList[settings.input]}</div>
          <CircleButton
            icon="CheckIcon"
            className="text-white bg-action p-[3px] rounded-[8px]"
            size={15}
            onClick={saveValueHandler}
          />
        </div>
      ) : (
        <div className={`flex items-center gap-[10px] ${withPadding && 'px-[20px]'}`}>
          <div className="relative flex items-center h-[40px]">
            <div
              ref={textRef}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              style={{ maxWidth: columnWidth ? columnWidth * 20 : valueMaxWidth + 'px' }}
              className="truncate !text-[14px] flex items-center"
            >
              {valueCreator[String(settings?.input)] || value}
            </div>
            {showTooltip && (
              <div
                className="
      absolute z-[9999] bg-white text-black
      border border-gray-300 rounded-[10px]
      px-[5px] py-[2px] shadow-md transform -translate-x-1/2
      w-[400px]  whitespace-normal break-words
    "
                style={{
                  top: '100%',
                  left: '50%',
                  marginTop: '5px',
                }}
              >
                {valueCreator[String(settings?.input)] || value}
              </div>
            )}
          </div>
          <CircleButton icon="EditIcon" size={15} onClick={changeEditMode.bind(null, true)} />
        </div>
      )}
    </div>
  );
});
MetricEditValue.displayName = 'MetricEditValue';
export default MetricEditValue;
